import repositories from '@/repositories'
import { baseGetParams, InvoiceParams } from '@/utils/api'
import { useTransactionResources } from '@/use/transactional-resources'
import { useInvoices } from '@/use/invoices'
import { INVOICE_RESOURCE_TYPE, OUTBOUND_INVOICE } from '@/constants/resource-types'
import { outboundActionData } from '@/constants/actions-by-resource/invoices/outbound'
import { Invoice, InvoiceEmailParams } from '@/types/interfaces/api-v2/invoice'
import { INVOICE_INCLUDE } from '@/constants/include'
import { AutocompletePayload, AutocompleteResponse } from '@/types/interfaces/api-v2/autocomplete'
import { useDetailsPanel } from '../details-panel'
import { useToasts } from '@/use/toasts'
import { SUCCESS, ERROR } from '@/constants/icons'
import { MarginPercentageResponse } from '@/types/interfaces/api-v2/margin-percentage'
import { InvoiceWorkOrderDetailsResponse } from '@/types/interfaces/api-v2/invoice/work-order-details'
import { InvoiceTripDetailsResponse } from '@/types/interfaces/api-v2/invoice/trip-details'
import { ref } from 'vue'
import { useSession } from '../session'
import { CLIENT, BROKER, VENDOR } from '@/constants/permissions'
import { useApp } from '@/use/app'

interface InvoiceUpdateParams {
  invoiceId: string
  updateContent: any,
  include?: string,
  objectScope?: 'both' | 'single' | 'multiple'
}

const { addToast } = useToasts()
const exploreOutboundInvoicesRef = ref<any>(null)
const { companyType } = useApp()

export const useOutboundInvoices = (emit?: any) => {
  const { invoiceActionModalProps, invoiceActionListenersAndFunctions, SAVED_INVOICE_STATUSES, flattenBatchInvoice } = useInvoices()
  const {
    sharedBasicActions,
    basicOutboundActions,
    resourceModalConfirmationHandler
  } = useTransactionResources()
  const basicInvoiceOutboundActions = [
    ...basicOutboundActions(invoiceActionListenersAndFunctions),
    {
      value: 'receive-payment',
      bulkConfig: {
        type: 'primary'
      },
      actionFn: (invoices: any[], additionalParams: any) => {
        invoiceActionListenersAndFunctions['receive-payment'](
          invoices, additionalParams
        )
      }
    },
    {
      value: 'save',
      bulkConfig: {
      }
    },
    {
      value: 'save-as-draft',
      bulkConfig: {
      }
    },
    {
      value: 'save-and-approve-to-send',
      bulkConfig: {
      }
    },
    {
      value: 'save-and-mark-sent',
      bulkConfig: {
      }
    },
    {
      value: 'save-and-send',
      bulkConfig: {
      }
    },
    {
      value: 'skip',
      bulkConfig: {
      }
    },
    {
      value: 'download',
      bulkConfig: {
      },
      actionFn: (proposals: any[], additionalParams: any) => {
        invoiceActionListenersAndFunctions.download(
          proposals,
          additionalParams,
          true

        )
      },
      noSuccessEmit: true
    },
    {
      value: 'view',
      updateSelectedResource: true,
      actionFn: (invoices: any) => {
        const { openGlobalDetailsPanel } = useDetailsPanel()
        const { outboundInvoiceModalRequiredActions, basicInvoiceOutboundActions } = useOutboundInvoices()
        openGlobalDetailsPanel('uc/resource-details', {
          actions: [
            ...basicInvoiceOutboundActions,
            ...outboundInvoiceModalRequiredActions
          ],
          selectedResources: invoices,
          resourceType: INVOICE_RESOURCE_TYPE,
          canDoubleClickToEdit: true,
          outbound: true
        }, '', { }, { })
      },
      noSuccessEmit: true
    },
    ...sharedBasicActions(invoiceActionListenersAndFunctions)
  ].filter((action:any) => companyType.value === VENDOR ? action.value !== 'receive-payment' : action)

  const outboundActionRequiredModals = [
    {
      value: 'delete',
      requiresConfirmation: true,
      bulkConfig: {
      },
      modal: invoiceActionModalProps.delete,
      actionFn: async (invoices: any[], additionalParams: any) => {
        await deleteOutboundInvoice(invoices[0].id, 'both')
        emit('change:focus', null)
      }
    },
    {
      value: 'void',
      requiresConfirmation: true,
      exportQueryRequired: true,
      canUndo: true,
      bulkConfig: {
        type: 'menu',
        textColor: 'error'
      },
      advancedModal: 'invoice/modals/void',
      dynamicContentProps: {
        allowedStatuses: outboundActionData.find((action: any) => action.name === 'Void').validStatuses,
        resultingStatus: '',
        numberIdKey: 'invoiceNumber',
        outbound: true
      }
    },
    {
      value: 'mark-sent',
      requiresConfirmation: true,
      canUndo: true,
      bulkConfig: {
        type: companyType.value === VENDOR ? 'primary' : 'menu'
      },
      modal: invoiceActionModalProps.markSent,
      actionFn: async (invoices: any[], additionalParams: any) => {
        invoices = invoices.map((invoice:any) => {
          if (invoice.props) {
            return invoice.props.invoice
          } else return invoice
        })
        const allowedStatuses = outboundActionData.find((action: {name: string}) => action.name === 'Mark Sent').validStatuses
        if (invoices.length === 1 && allowedStatuses.includes(invoices[0]?.status)) {
          await updateInvoice({ invoiceId: invoices[0].id, updateContent: { status: 'sent', confirm: true, markSent: true }, include: INVOICE_INCLUDE, objectScope: 'both' })
        } else {
          additionalParams = { ...additionalParams, confirm: true, markSent: true }
          resourceModalConfirmationHandler('mark-sent', invoices, additionalParams, INVOICE_RESOURCE_TYPE, patchInvoicesBulkUpdate, { directionalActionData: outboundActionData, statuses: SAVED_INVOICE_STATUSES }, true)
        }
      }
    },
    {
      value: 'assign-department',
      requiresConfirmation: true,
      canUndo: true,
      bulkConfig: {
        type: 'menu'
      },
      modal: invoiceActionModalProps.assignDepartment,
      actionFn: (invoices: any[], additionalParams: any) => {
        invoices = invoices.map((invoice:any) => {
          if (invoice.props) {
            return invoice.props.invoice
          } else return invoice
        })
        resourceModalConfirmationHandler('assign-department', invoices, additionalParams, INVOICE_RESOURCE_TYPE, invoices.length > 1 ? patchInvoicesBulkUpdate : updateInvoice, { directionalActionData: outboundActionData, statuses: SAVED_INVOICE_STATUSES }, true)
      }
    },
    {
      value: 'approve-to-send',
      requiresConfirmation: true,
      canUndo: true,
      bulkConfig: {
        type: 'menu'
      },
      modal: invoiceActionModalProps.approveToSend,
      actionFn: async (invoices: any[], additionalParams: any) => {
        invoices = invoices.map((invoice:any) => {
          if (invoice.props) {
            return invoice.props.invoice
          } else return invoice
        })
        if (invoices.length === 1) {
          await updateInvoice({ invoiceId: invoices[0].id, updateContent: { status: 'approved_to_send' }, include: INVOICE_INCLUDE, objectScope: 'both' })
        } else {
          resourceModalConfirmationHandler('approve-to-send', invoices, additionalParams, INVOICE_RESOURCE_TYPE, patchInvoicesBulkUpdate, { directionalActionData: outboundActionData, statuses: SAVED_INVOICE_STATUSES }, true)
        }
      }
    },
    // display Change Template on menu options for outbound invoices
    {
      value: 'change-template',
      requiresConfirmation: true,
      updateSelectedResource: false,
      advancedModal: 'uc/change-template',
      dynamicContentProps: {
        type: OUTBOUND_INVOICE
      }
    },
    {
      value: 'reject',
      requiresConfirmation: true,
      updateSelectedResource: true,
      advancedModal: 'invoice/modals/vendor-reject'
    }
  ]

  const getApproveOrConfirmActions = (): any[] => {
    const { session } = useSession()
    if ([BROKER, CLIENT].includes(session.currentCompanyType)) return []

    const confirmDenyAction = [
      {
        value: 'confirm',
        requiresConfirmation: true,
        canUndo: true,
        bulkConfig: {
          type: 'menu',
          textColor: 'success'
        },
        exportQueryRequired: true,
        advancedModal: 'uc/resource/modal-content/confirm-deny',
        dynamicContentProps: {
          allowedStatuses: outboundActionData.find((action: any) => action.name === 'Confirm').validStatuses,
          numberIdKey: 'invoiceNumber',
          tableRef: exploreOutboundInvoicesRef
        }
      },
      {
        value: 'deny',
        requiresConfirmation: true,
        exportQueryRequired: true,
        advancedModal: 'uc/resource/modal-content/confirm-deny',
        dynamicContentProps: {
          allowedStatuses: outboundActionData.find((action: any) => action.name === 'Deny').validStatuses,
          numberIdKey: 'invoiceNumber',
          tableRef: exploreOutboundInvoicesRef,
          isConfirm: false
        }
      }
    ]

    return confirmDenyAction
  }

  const outboundInvoiceModalRequiredActions = [
    ...outboundActionRequiredModals,
    getApproveOrConfirmActions()
  ].flat(3)

  const getInvoices = async (params: any) => {
    try {
      const res: any = await repositories.invoices.get(params)
      return {
        data: res.invoices,
        totalPages: res.meta.totalPages,
        totalCount: res.meta.totalCount
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const invoicesSearch = async (params: any) => {
    try {
      const res: any = await repositories.invoices.invoicesSearch(params)
      return {
        data: res.invoices,
        totalPages: res.meta.totalPages,
        totalCount: res.meta.totalCount,
        nextPage: res.meta.nextPage
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const deleteInvoiceWithId = async (invoiceId: string, isVendor: boolean) => {
    try {
      isVendor ? await repositories.invoices.deleteInboundInvoiceWithId(invoiceId) : await repositories.invoices.deleteOutboundInvoiceWithId(invoiceId)
    } catch (error) {
      console.log(error)
      return 'error'
    }
  }

  const deleteOutboundInvoice = async (invoiceId: string, objectScope = 'both'): Promise<{error?: boolean, success?: string}> => {
    try {
      return await repositories.invoices.deleteOutboundInvoice(invoiceId, objectScope)
    } catch (err) {
      return { error: true }
    }
  }

  const getOutboundInvoiceNotes = async (invoiceId: string | number) => {
    try {
      const res = await repositories.invoices.getOutboundInvoiceNotes(invoiceId)
      return res
    } catch (err: any) {
      console.error(err)
    }
  }

  const patchInvoicesBulkUpdate = async (params: any) => {
    try {
      if (!(params.query || params.ids) || !params.field || !params.value) throw new Error('(Id array or q) and new status required to call this endpoint')
      const res = await repositories.invoices.patchBulkUpdate(params)
      return res
    } catch (err: any) {
      console.error(err)
      return false
    }
  }

  const postInvoicesSendBulkEmail = async (body: any, objectScope: 'both' | 'multiple' | 'single') => {
    try {
      // if (!ids) throw new Error('Id array required in params')
      const invoiceEmail = await repositories.invoices.postSendBulkEmail(body, objectScope)
      return { data: invoiceEmail.successInvoices || false }
    } catch (err: any) {
      console.error(err)
      return null
    }
  }

  const postInvoiceSendEmail = async (
    invoiceId: number, invoiceEmail: InvoiceEmailParams,
    objectScope: string, confirm?: boolean
  ) => {
    try {
      const res = await repositories.invoices.sendEmail(
        invoiceId, invoiceEmail,
        objectScope, confirm
      )
      return res
    } catch (err: any) {
      console.error(err)
    }
  }

  const getInvoicesNew = async (params: baseGetParams) => {
    try {
      const res = await repositories.invoices.new(params)
      return res
    } catch (err: any) {
      console.error(err)
    }
  }

  const postBatchInvoices = async (params: baseGetParams & { withTripIds?: boolean }, requestBody:any) => {
    try {
      const res = await repositories.invoices.newBatch(params, requestBody)
      return res
    } catch (err: any) {
      console.error(err)
      return err
    }
  }

  const getInvoiceById = async (id: number, params: InvoiceParams) => {
    try {
      const res = await repositories.invoices.getById(id, params)
      return { data: res.invoice }
    } catch (err: any) {
      console.error(err)
      return { data: false }
    }
  }

  const postInvoices = async (params: any, requestBody: any) => {
    try {
      let res:any
      if (requestBody.invoice.grouped) {
        res = await repositories.batchInvoices.post(params, requestBody)
        return flattenBatchInvoice(res)
      } else {
        res = await repositories.invoices.post(params, requestBody)
        return res.invoice
      }
    } catch (err: any) {
      console.error(err)
      return err
    }
  }

  const updateInvoice = async (
    { invoiceId, updateContent, include, objectScope }: InvoiceUpdateParams
  ) => {
    try {
      let res:any
      if (updateContent.grouped) {
        res = await repositories.batchInvoices.update({ invoiceId, updateContent, include, objectScope })
        return { invoice: flattenBatchInvoice(res) }
      } else {
        return await repositories.invoices.update({ invoiceId, updateContent, include, objectScope })
      }
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const forwardInvoice = async (invoiceId: number, markup: number, addMarkupToInvoice:boolean, include = '') => {
    try {
      const res = await repositories.invoices.forwardInvoice(invoiceId, markup, addMarkupToInvoice, include)
      return res
    } catch (err: any) {
      console.error(err)
      return { data: false }
    }
  }

  // must pass in YYYY/MM/DD format
  const totalInvoiceAmount = async (startEndDate: any) => {
    try {
      return await repositories.invoices.totalAmount(startEndDate)
    } catch (e) {
      console.log(e)
      return false
    }
  }

  const INVOICE_GROUP_BY_CONFIG_OPTIONS = [{ id: 2, name: 'Client' }, { id: 0, name: 'Site' }, { id: 1, name: 'Work Order' }, { id: 3, name: 'Event' }]

  const getBatchInvoiceById = async (invoiceId: string | number, include: string) => {
    try {
      const res = await repositories.batchInvoices.getById(Number(invoiceId), { include })
      return { data: flattenBatchInvoice(res) }
    } catch (err: any) {
      console.error(err)
      return { data: false }
    }
  }

  const invoicesAutoComplete = async (params: AutocompletePayload) => {
    try {
      const res: any = await repositories.invoices.invoicesAutoComplete(params)
      return {
        data: res.results,
        nextPage: res.nextPage
      } as { data: AutocompleteResponse[], nextPage: number }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const fetchWorkOrdersDescription = async (workOrderId: number | string, tripIds: (number | string)[], invoiceId: number | string) => {
    try {
      const res: any = await repositories.workOrders.fetchWorkOrdersDescription(workOrderId, tripIds, invoiceId)
      return {
        data: res.description
      } as { data: string }
    } catch (err) {
      console.log(err)
      return { data: '' }
    }
  }

  const actionableInvoices = async (params: { ids?: number[] | string[], q?: any[], status: string }) => {
    try {
      const res: { actionableIds: number[], nonActionableIds: number[] } = await repositories.invoices.actionableInvoices(params)
      return {
        data: res
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const marginPercentage = async (params: { ids: number[], objectScope?: string, includeGrouped?: boolean, withoutCount?: boolean }) => {
    try {
      const res: { invoices: MarginPercentageResponse[] } = await repositories.invoices.marginPercentage(params)
      return res
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const invoicesCount = async (params: InvoiceParams) => {
    try {
      const res = await repositories.invoices.count(params)
      return {
        count: res.count
      }
    } catch (err) {
      console.log(err)
      return { count: 0 }
    }
  }

  const additionalDataDetails = [
    {
      fetchFn: marginPercentage,
      responseKey: 'invoices',
      dataKey: 'marginPercentage',
      fetchParams: { objectScope: 'both', includeGrouped: true, withoutCount: true }
    },
    {
      fetchFn: invoiceWorkOrderDetails,
      responseKey: 'invoices',
      dataKey: 'workOrderInfo',
      fetchParams: { objectScope: 'both', includeGrouped: true, withoutCount: true }
    },
    {
      fetchFn: invoiceTripDetails,
      responseKey: 'invoices',
      dataKey: 'tripInfo',
      fetchParams: { objectScope: 'both', includeGrouped: true, withoutCount: true }
    }
  ]

  const statuses = async (as = 'both', inbound: boolean) => {
    try {
      const res = await repositories.invoices.invoiceStatuses(as, inbound)
      return {
        data: res.statuses
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  async function invoiceWorkOrderDetails (params: { ids: number[], objectScope?: string, includeGrouped?: boolean, withoutCount?: boolean }) {
    try {
      const res: { invoices: InvoiceWorkOrderDetailsResponse[] } = await repositories.invoices.invoiceWorkOrderDetails(params)
      return res
    } catch (err) {
      return { data: false }
    }
  }

  async function invoiceTripDetails (params: { ids: number[], objectScope?: string, includeGrouped?: boolean, withoutCount?: boolean }) {
    try {
      const res: { invoices: InvoiceTripDetailsResponse[] } = await repositories.invoices.invoiceTripDetails(params)
      return res
    } catch (err) {
      return { data: false }
    }
  }

  async function invoiceWorkOrders (params: { invoiceId: number, perPage?: number, withoutCount?: boolean, include?: string, objectScope?: string, includeGrouped?: boolean }) {
    try {
      const res = await repositories.invoices.invoiceWorkOrders(params)
      return {
        data: res.workOrders,
        totalPages: res.meta.totalPages,
        totalCount: res.meta.totalCount,
        nextPage: res.meta.nextPage
      }
    } catch (err) {
      return { data: false }
    }
  }

  async function invoiceTrips (params: { invoiceId: number, perPage?: number, include?: string, workOrderId?: number | number[], objectScope?: string, includeGrouped?: boolean, withoutCount?: boolean }) {
    try {
      const res = await repositories.invoices.invoiceTrips(params)
      return {
        data: res.trips,
        totalPages: res.meta.totalPages,
        totalCount: res.meta.totalCount,
        nextPage: res.meta.nextPage
      }
    } catch (err) {
      return { data: false }
    }
  }

  return {
    deleteInvoiceWithId,
    getInvoices,
    deleteOutboundInvoice,
    updateInvoice,
    patchInvoicesBulkUpdate,
    getOutboundInvoiceNotes,
    postInvoicesSendBulkEmail,
    getInvoicesNew,
    postInvoices,
    getInvoiceById,
    postBatchInvoices,
    forwardInvoice,
    basicInvoiceOutboundActions,
    outboundInvoiceModalRequiredActions,
    totalInvoiceAmount,
    INVOICE_GROUP_BY_CONFIG_OPTIONS,
    postInvoiceSendEmail,
    getBatchInvoiceById,
    invoicesAutoComplete,
    fetchWorkOrdersDescription,
    invoicesSearch,
    actionableInvoices,
    marginPercentage,
    invoicesCount,
    additionalDataDetails,
    statuses,
    invoiceWorkOrders,
    invoiceTrips,
    exploreOutboundInvoicesRef
  }
}
